// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The rendered-outputs pointer list for a cycle (list_reporting_artifacts). A row shows the
// kind, its sensitivity tier, the frozen artifact hash and size. The Q2 fixture sits at the
// gate with NO artifacts rendered yet (the render-before-gate gap BLK-2 notes), so this
// correctly shows the empty state.
//
// OBJECT BYTES: still never fetched on THIS tab — the list is a pointer list. The one place in
// the island that does fetch bytes is the Newsletter tab, because a retail financial promotion
// has to be READ by a named human before it can be sent, and this row's hash + size is not
// reading it. The newsletter row therefore links across rather than trying to preview inline;
// decks stay pointer-only.

import { Link, useOutletContext } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { FileText, Eye } from "lucide-react";
import { LoadingState, ErrorState, EmptyState } from "@/components/research-hub";
import { SensitivityTierBadge } from "@/components/investment-reporting";
import { useReportingArtifacts } from "@/hooks/investment-reporting/useReportingArtifacts";
import { REPORTING_BASE, type CycleContext } from "./ReportingCycleLayout";

const KIND_LABEL: Record<string, string> = {
  ir: "Canonical IR (JSON)",
  react_view: "React report view",
  deck_ic: "IC deck",
  deck_exec: "Exec deck",
  deck_ext_ic: "External IC deck",
  deck_adviser: "Adviser deck",
  deck_retail: "Retail webinar deck",
  newsletter: "Retail newsletter",
};

export default function ArtifactsTab() {
  const { cycle } = useOutletContext<CycleContext>();
  const { data, isLoading, error, refetch } = useReportingArtifacts(cycle.id);

  if (isLoading) return <LoadingState rows={3} />;
  if (error) return <ErrorState onRetry={() => refetch()} />;

  const rows = data ?? [];
  if (rows.length === 0)
    return (
      <EmptyState
        title="No artifacts rendered yet."
        body="Decks and the React view are rendered before the gate; this cycle has not reached render."
      />
    );

  return (
    <div className="space-y-2">
      {rows.map((a, i) => (
        <Card key={a.id ?? i} className="p-4 flex items-center justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <FileText className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" aria-hidden />
            <div className="min-w-0">
              <div className="font-body text-sm font-medium text-foreground">
                {KIND_LABEL[a.kind ?? ""] ?? a.kind}
              </div>
              <div className="font-body text-[11px] text-muted-foreground truncate">
                {a.artifact_sha256 ? `sha256 ${a.artifact_sha256.slice(0, 12)}…` : "no frozen hash"}
                {a.bytes != null ? ` · ${formatBytes(a.bytes)}` : ""}
                {a.render_engine_version ? ` · ${a.render_engine_version}` : ""}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {a.kind === "newsletter" && (
              <Link
                to={`${REPORTING_BASE}/${cycle.id}/newsletter`}
                className="inline-flex items-center gap-1 font-body text-xs text-primary hover:underline"
                title="Read the rendered copy and record the content review"
              >
                <Eye className="h-3.5 w-3.5" aria-hidden /> Review copy
              </Link>
            )}
            <SensitivityTierBadge tier={a.sensitivity_tier} />
          </div>
        </Card>
      ))}
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
